import { Box, CircularProgress } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import {
  InteractionSummary,
  SummaryStatus,
} from "../../../types/summary/Summary";
import { AuthContext } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import {
  useGenerateSummary,
  type TranscribeAndSummarizePhase,
} from "../../../hooks/useSummary";
import InlineTemplatePicker from "./InlineTemplatePicker";

export const archieButtonSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "#fff",
  background: "var(--archie-gradient)",
  border: "none",
  borderRadius: "8px",
  px: 2.5,
  py: 1,
  cursor: "pointer",
  fontFamily: "inherit",
  "&:hover": { opacity: 0.88 },
};

export const archieIconSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    style={{ width: 14, height: 14 }}
  >
    <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" />
    <circle cx="9" cy="13" r="1" />
    <circle cx="15" cy="13" r="1" />
  </svg>
);

interface SummaryTabProps {
  customerId: string;
  interactionId: string;
  summary?: InteractionSummary | null;
  isLoading: boolean;
  hasTranscript: boolean;
  hasMedia: boolean;
  interactionType: string;
  onSummaryRequested?: () => void;
  /** Starts the transcribe→summarize flow (hosted by the parent so it
   *  survives tab switches). Called for interactions with no transcript yet. */
  onTranscribeAndSummarize: (templateId: string) => void;
  /** Current phase of the parent-hosted transcribe→summarize flow. */
  transcribeSummarizePhase?: TranscribeAndSummarizePhase;
}

export default function SummaryTab({
  customerId,
  interactionId,
  summary,
  isLoading,
  hasTranscript,
  hasMedia,
  interactionType,
  onSummaryRequested,
  onTranscribeAndSummarize,
  transcribeSummarizePhase,
}: SummaryTabProps) {
  const { t } = useTranslation(["common", "summary"]);
  const { user } = useContext(AuthContext);
  const showAlert = useAlert();
  const [requestSent, setRequestSent] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const generateSummary = useGenerateSummary();

  const canSummarize =
    (user?.hasPermission("interactions:Replay") ||
      user?.hasPermission("interactions:ReplayRaw")) &&
    user?.hasPermission("summary-templates:Read");

  const canTranscribe =
    ["Audio", "Video", "AudioAndVideo"].includes(interactionType) && hasMedia;

  const handleSummarizeClick = () => {
    setShowTemplatePicker(true);
  };

  const handleTemplateSelected = (templateId: string) => {
    setShowTemplatePicker(false);

    if (hasTranscript) {
      setRequestSent(true);
      onSummaryRequested?.();
      generateSummary.mutate(
        { customerId, interactionId, templateId },
        {
          onError: (err: Error) => {
            setRequestSent(false);
            showAlert(err.message, "error");
          },
        },
      );
    } else {
      // No transcript yet: hand off to the parent-hosted flow, which
      // transcribes, then generates the summary once transcription completes.
      // Progress/errors are driven by transcribeSummarizePhase.
      onTranscribeAndSummarize(templateId);
    }
  };

  const handlePickerCancel = () => {
    setShowTemplatePicker(false);
  };

  const handleRegenerate = () => {
    setShowTemplatePicker(true);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 120,
          gap: 1,
        }}
      >
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
        >
          {t("loadingSummary")}
        </Box>
      </Box>
    );
  }

  const isCompleted =
    summary?.status === SummaryStatus.Completed && summary?.summaryText;
  const isInProgress = summary?.status === SummaryStatus.InProgress;
  const isFailed = summary?.status === SummaryStatus.Failed;
  const isTranscribing = transcribeSummarizePhase === "transcribing";
  const isSummarizing = transcribeSummarizePhase === "summarizing";
  const isPending =
    requestSent ||
    generateSummary.isPending ||
    isInProgress ||
    isTranscribing ||
    isSummarizing;

  // Summary available
  if (isCompleted) {
    return (
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Template picker above dimmed summary when regenerating */}
        {showTemplatePicker && (
          <Box
            sx={{
              borderRadius: "10px",
              border: "1px solid var(--border-mid)",
              overflow: "hidden",
            }}
          >
            <InlineTemplatePicker
              customerId={customerId}
              onSelect={handleTemplateSelected}
              onCancel={handlePickerCancel}
            />
          </Box>
        )}

        {/* AI summary card */}
        <Box
          sx={{
            borderRadius: "10px",
            overflow: "hidden",
            border: "1px solid var(--border-mid)",
            ...(showTemplatePicker && { opacity: 0.5 }),
          }}
        >
          {/* Header badge */}
          <Box
            sx={{
              background: "var(--archie-gradient)",
              px: 1.5,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 13, height: 13, color: "#fff", flexShrink: 0 }}
            >
              <path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z" />
              <circle cx="9" cy="13" r="1" />
              <circle cx="15" cy="13" r="1" />
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.625rem",
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.08em",
                flex: 1,
              }}
            >
              {t("summarizedByArchie")}
            </Box>
            {canSummarize && (
              <Box
                component="button"
                onClick={handleRegenerate}
                title={t("summary:resummarize")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.8)",
                  cursor: "pointer",
                  p: 0,
                  borderRadius: "4px",
                  "&:hover": { color: "#fff" },
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 13, height: 13 }}
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M3 22v-6h6" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
              </Box>
            )}
          </Box>

          {/* Summary text */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "var(--bg-raised)",
              fontSize: "0.8125rem",
              color: "var(--text-primary)",
              lineHeight: 1.6,
              "& p": { m: 0, mb: 1, "&:last-child": { mb: 0 } },
              "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
              "& li": { mb: 0.5 },
              "& strong": { fontWeight: 600 },
              "& h1, & h2, & h3, & h4": {
                m: 0,
                mb: 0.75,
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "var(--text-primary)",
              },
              "& code": {
                fontSize: "0.75rem",
                px: 0.5,
                py: 0.25,
                borderRadius: "4px",
                backgroundColor: "var(--bg-active)",
                fontFamily: "'JetBrains Mono', monospace",
              },
            }}
          >
            <ReactMarkdown>{summary.summaryText!}</ReactMarkdown>
          </Box>
        </Box>
      </Box>
    );
  }

  // Template picker shown (no existing summary)
  if (showTemplatePicker) {
    return (
      <Box
        sx={{
          borderRadius: "10px",
          border: "1px solid var(--border-mid)",
          mx: 2,
          mt: 2,
          overflow: "hidden",
        }}
      >
        <InlineTemplatePicker
          customerId={customerId}
          onSelect={handleTemplateSelected}
          onCancel={handlePickerCancel}
        />
      </Box>
    );
  }

  // No summary — show action buttons or status
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: 4,
        px: 2,
      }}
    >
      {isFailed && (
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--signal-red, #ef4444)",
            mb: 0.5,
          }}
        >
          {t("summary:failed")}
        </Box>
      )}

      {!isPending && !isFailed && (
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
            mb: 1,
          }}
        >
          {t("summary:noSummaryAvailable")}
        </Box>
      )}

      {!isPending && hasTranscript && (
        <Box
          component="button"
          onClick={canSummarize ? handleSummarizeClick : undefined}
          disabled={!canSummarize}
          title={!canSummarize ? t("summary:noPermission") : undefined}
          sx={{
            ...archieButtonSx,
            ...(!canSummarize && {
              opacity: 0.5,
              cursor: "not-allowed",
              "&:hover": { opacity: 0.5 },
            }),
          }}
        >
          {archieIconSvg}
          {t("summary:summarize")}
        </Box>
      )}

      {!isPending && !hasTranscript && canTranscribe && (
        <Box
          component="button"
          onClick={canSummarize ? handleSummarizeClick : undefined}
          disabled={!canSummarize}
          title={!canSummarize ? t("summary:noPermission") : undefined}
          sx={{
            ...archieButtonSx,
            ...(!canSummarize && {
              opacity: 0.5,
              cursor: "not-allowed",
              "&:hover": { opacity: 0.5 },
            }),
          }}
        >
          {archieIconSvg}
          {t("summary:transcribeAndSummarize")}
        </Box>
      )}

      {isPending && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Box
            component="span"
            sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
          >
            {isTranscribing
              ? t("summary:transcribing")
              : isInProgress || isSummarizing
                ? t("summary:summarizing")
                : t("summary:preparingSummary")}
          </Box>
        </Box>
      )}
    </Box>
  );
}
